import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import get from 'lodash/get'
import flowRight from 'lodash/flowRight'
import uniq from 'lodash/uniq'

import { ErrorView, Loading } from '@fielded/shared-ui'

import withConfig from '../../../../../../van-shared/hoc/withConfig'
import { withApi } from '../../../../../../common/ApiProvider'
import { withUser } from '../../../../../../common/AuthenticationProvider'
import withShipment from '../../../../common/WithShipmentWrapper'
import { batchIdToProductId } from '../../../../common/utils'
import { loadPickList } from '../../../../../../root/reducers/shipments/pick-list'
import { useAmplitudeAnalytics } from '../../../../../../common/analytics-tracking/AmplitudeAnalyticsProvider'

import LMDDeliveryDetail from './LMDDeliveryDetail'
import { buildProductsForPickList } from './utils'

const LMDDeliveryDetailContainer = ({
  api,
  user,
  config,
  shipment
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const trackingHandler = useAmplitudeAnalytics()

  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const [productsMap, setProductsMap] = useState(new Map())
  const [appPrograms, setAppPrograms] = useState({})
  const [sourceName, setSourceName] = useState('')

  // `withShipment` loads the shipment before this container renders.
  const delivery = shipment

  const initialize = async () => {
    try {
      const shipmentProductIds = Object.keys(delivery.counts).map(batchId => batchIdToProductId(batchId))
      const productIds = uniq(shipmentProductIds.map(productId => productId.toLowerCase()))
      const [products, appProgram, origin] = await Promise.all([
        api.product.getByIds(productIds),
        api.program.get(delivery.programId),
        api.location.get(delivery.origin.id)
      ])

      // The performance problem on this route was `withMasterData`, which blocks the page on
      // global shipments master data and the expensive `api.batch.list()` call.
      //
      // We still dispatch `loadPickList()` here because PR #17606 added an EPN flow from this
      // screen into shipment options -> remove products. `DeleteProductsContainer` reads the
      // pick list from Redux, but it does not build that state itself.
      //
      // So the fix is: keep the preload, but scope it to this shipment's products only.
      await dispatch(loadPickList(
        delivery,
        buildProductsForPickList(products, shipmentProductIds),
        user
      ))

      const productsMap = new Map(products.map(product => [product._id, product]))
      const sourceName = get(origin, 'name', delivery.origin.id)

      setProductsMap(productsMap)
      setAppPrograms([appProgram])
      setSourceName(sourceName)

      trackingHandler.shipment.viewed({ shipment })
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  if (error) {
    return <ErrorView title='Shipment' error={error} />
  }

  if (loading) {
    return <Loading />
  }

  return (
    <LMDDeliveryDetail
      user={user}
      history={history}
      config={config}
      delivery={delivery}
      productsMap={productsMap}
      appPrograms={appPrograms}
      sourceName={sourceName}
    />
  )
}

const withHOCs = flowRight(
  withApi,
  withUser,
  withConfig,
  withShipment
)

export default withHOCs(LMDDeliveryDetailContainer)
